// Taxonomía de movimientos del ledger (FRA-343). El sistema deja de saber
// decir solo "ingreso o gasto": cada movimiento declara si mueve caja y qué
// toca, en un solo lugar — no repartido en las pantallas.
//
//   Movimiento                  Caja   Qué toca
//   Pago de cliente              +$    Ingreso
//   Costo / gasto / compra       −$    Gasto
//   Comisión de planner          −$    Gasto (del evento), etiquetado aparte
//   Depósito recibido            +$    Comprometido (lo tienes, lo debes)
//   Depósito devuelto            −$    Baja lo comprometido
//   Depósito aplicado             —    Comprometido → ingreso
//   Reembolso                    −$    Reversión de ingreso
//   Devolución de proveedor      +$    Reversión de gasto (saldo a favor
//                                      de un evento cancelado que regresó)
//   Retiro                       −$    Repartición de utilidades, no gasto
//
// El retiro NO es un costo: vive en su propia colección (venues/{id}/retiros),
// nunca cuelga de un evento y no toca ningún reporte de resultados. Solo
// existe para la cuenta corriente — saber cuánto hay en la cuenta.
//
// Los depósitos vienen de dos fuentes y las dos son dinero que el venue
// RECIBE y tiene en custodia: los del cliente del evento (clienteDepositos)
// y los que deja el proveedor (proveedores/{pid}/depositos). Aplicar un
// depósito es quedárselo por un problema: es ingreso del venue, no un pago
// del evento — no abona a totalPagado ni baja Por cobrar.
//
// Un costo `por pagar` NO genera asiento: nada ha pasado todavía. Vive en
// Por pagar y entra al ledger el día que se paga, con su fecha de pago.
//
// Cada asiento trae `metodo`: cómo entró o salió el dinero (transferencia,
// efectivo…). Es una columna propia en las tablas, no parte del `detalle`.
//
// Módulo puro: sin Firestore ni React, como utils/costos.js.

import { fechaPagoDe, esComisionPlanner, devolucionDe } from "./costos";
import { normalizeDepositoEstado } from "./depositoEstado";

export const ETIQUETAS_ASIENTO = {
  pago: "Pago",
  reembolso: "Reembolso",
  gasto: "Gasto",
  comision_planner: "Comisión de planner",
  retiro: "Retiro",
  deposito_recibido: "Depósito recibido",
  deposito_devuelto: "Depósito devuelto",
  deposito_aplicado: "Depósito aplicado",
  devolucion_proveedor: "Devolución de proveedor",
};

const n = (v) => Number(v) || 0;

// Un depósito produce hasta tres asientos: recibirlo siempre es uno; su
// desenlace agrega la devolución y/o la aplicación. Una devolución parcial
// es dos asientos a la vez — la porción devuelta baja lo comprometido y el
// resto se vuelve ingreso — con la misma fecha (`fechaDevolucion` sirve
// para ambos estados en los docs actuales). Los dos llevan `parcial: true` y
// `montoAplicado`, para que las listas lo digan.
//
// `orden` desempata el mismo día: los tres asientos comparten el createdAt
// del depósito, así que sin esto una devolución el mismo día del depósito
// cae en orden arbitrario. Recibido (0) ocurre antes que su desenlace (1, 2).
export function asientosDeDeposito(deposito) {
  if (!deposito) return [];
  const monto = n(deposito.monto);
  if (monto <= 0) return [];
  const estado = normalizeDepositoEstado(deposito.estado);
  const fuente = deposito.proveedorId ? "proveedor" : "cliente";
  const base = {
    eventId: deposito.eventId || null,
    proveedorId: deposito.proveedorId || null,
    proveedorNombre: deposito.proveedorNombre || null,
    concepto:
      deposito.descripcion ||
      (fuente === "proveedor"
        ? `Depósito de ${deposito.proveedorNombre || "proveedor"}`
        : "Depósito en garantía"),
    detalle: fuente === "proveedor" ? "De proveedor" : "De cliente",
    fuente,
    createdAt: deposito.createdAt || null,
    origenDoc: deposito,
  };

  const asientos = [
    {
      ...base,
      id: `dep-recibido-${fuente}-${deposito.id}`,
      tipo: "deposito_recibido",
      fecha: deposito.fecha || deposito.createdAt || null,
      metodo: deposito.metodo || null,
      caja: monto,
      monto,
      orden: 0,
    },
  ];

  if (estado === "devuelto") {
    const devuelto = Math.min(
      monto,
      deposito.montoDevuelto == null ? monto : n(deposito.montoDevuelto)
    );
    const aplicado = monto - devuelto;
    const parcial = devuelto > 0 && aplicado > 0;
    if (devuelto > 0) {
      asientos.push({
        ...base,
        id: `dep-devuelto-${fuente}-${deposito.id}`,
        tipo: "deposito_devuelto",
        fecha: deposito.fechaDevolucion || null,
        metodo: deposito.metodoDevolucion || null,
        caja: -devuelto,
        monto: devuelto,
        orden: 1,
        parcial,
        montoAplicado: parcial ? aplicado : 0,
      });
    }
    if (aplicado > 0) {
      asientos.push({
        ...base,
        id: `dep-aplicado-${fuente}-${deposito.id}`,
        tipo: "deposito_aplicado",
        fecha: deposito.fechaDevolucion || null,
        metodo: null,
        caja: 0,
        monto: aplicado,
        orden: 2,
        parcial,
        montoAplicado: aplicado,
      });
    }
  } else if (estado === "aplicado") {
    asientos.push({
      ...base,
      id: `dep-aplicado-${fuente}-${deposito.id}`,
      tipo: "deposito_aplicado",
      fecha: deposito.fechaDevolucion || null,
      metodo: null,
      caja: 0,
      monto,
      orden: 2,
      parcial: false,
      montoAplicado: monto,
    });
  }

  return asientos;
}

// Construye el ledger completo del venue. `depositos` es la lista mezclada
// de las dos fuentes (los de proveedor traen proveedorId/proveedorNombre,
// como los entrega loadProveedorDepositos). `retiros` es la colección
// venues/{id}/retiros tal cual.
export function asientosDelLedger({
  payments = [],
  refunds = [],
  costos = [],
  depositos = [],
  retiros = [],
} = {}) {
  const out = [];

  (payments || []).forEach((p) => {
    const monto = n(p.monto);
    out.push({
      id: `pago-${p.id}`,
      tipo: "pago",
      fecha: p.fecha,
      caja: monto,
      monto,
      concepto: p.notas || "Pago",
      detalle: null,
      metodo: p.metodo || null,
      eventId: p.eventId || null,
      proveedorId: null,
      createdAt: p.createdAt || null,
      origenDoc: p,
    });
  });

  (refunds || []).forEach((r) => {
    const monto = n(r.monto);
    out.push({
      id: `reembolso-${r.id}`,
      tipo: "reembolso",
      fecha: r.fecha,
      caja: -monto,
      monto,
      concepto: r.notas || "Reembolso",
      detalle: null,
      metodo: r.metodo || null,
      eventId: r.eventId || null,
      proveedorId: null,
      createdAt: r.createdAt || null,
      origenDoc: r,
    });
  });

  (costos || []).forEach((c) => {
    const monto = n(c.monto);
    // Un costo por pagar no es movimiento: vive en Por pagar hasta cubrirse.
    const fechaPago = fechaPagoDe(c);
    if (!fechaPago) return;
    // La comisión del planner sigue siendo gasto (baja la utilidad del
    // evento); solo cambia la etiqueta, porque es dinero que se le manda
    // al planner y el venue quiere reconocerlo de un vistazo.
    const comision = esComisionPlanner(c);
    out.push({
      id: `gasto-${c.id}`,
      tipo: comision ? "comision_planner" : "gasto",
      fecha: fechaPago,
      caja: -monto,
      monto,
      concepto: c.concepto || "Costo",
      detalle: comision ? c.proveedorNombre || null : c.categoria || null,
      metodo: c.metodo || null,
      eventId: c.eventId || null,
      proveedorId: c.proveedorId || null,
      proveedorNombre: c.proveedorNombre || null,
      createdAt: c.createdAt || null,
      origenDoc: c,
    });
    // El proveedor regresó (todo o parte de) lo pagado por un evento
    // cancelado: entra a caja como reversión del gasto, con su fecha.
    const devolucion = devolucionDe(c);
    if (devolucion) {
      out.push({
        id: `devolucion-${c.id}`,
        tipo: "devolucion_proveedor",
        fecha: devolucion.fecha,
        caja: devolucion.monto,
        monto: devolucion.monto,
        concepto: `Devolución de ${c.proveedorNombre || "proveedor"}`,
        detalle: c.concepto || null,
        metodo: c.saldoAFavor?.metodoDevolucion || null,
        eventId: c.eventId || null,
        proveedorId: c.proveedorId || null,
        proveedorNombre: c.proveedorNombre || null,
        createdAt: c.saldoAFavor?.resueltoAt || c.createdAt || null,
        origenDoc: c,
      });
    }
  });

  (depositos || []).forEach((d) => {
    out.push(...asientosDeDeposito(d));
  });

  // El retiro baja la caja (es la cuenta corriente del venue) sin tocar la
  // utilidad de eventos ni el estado de resultados: es repartición de
  // utilidades, no gasto de operación.
  (retiros || []).forEach((r) => {
    const monto = n(r.monto);
    if (monto <= 0) return;
    out.push({
      id: `retiro-${r.id}`,
      tipo: "retiro",
      fecha: r.fecha,
      caja: -monto,
      monto,
      concepto: r.concepto || "Retiro",
      detalle: null,
      metodo: r.metodo || null,
      eventId: null,
      proveedorId: null,
      createdAt: r.createdAt || null,
      origenDoc: r,
    });
  });

  return out;
}

// Solo lo que movió dinero: es lo que Movimientos lista y lo que suma el
// flujo. La aplicación de un depósito queda fuera — reclasifica, no mueve
// caja.
export const movimientosDeCaja = (asientos) =>
  (asientos || []).filter((a) => a.caja !== 0);

// Las dos cifras del encabezado de Cuentas: cuánto hay en la cuenta y
// cuánto de eso está comprometido. Tener $180,000 con $40,000 en depósitos
// activos es tener $140,000 propios.
export function resumenLedger(asientos) {
  let saldo = 0;
  let comprometido = 0;
  (asientos || []).forEach((a) => {
    saldo += a.caja;
    if (a.tipo === "deposito_recibido") comprometido += a.monto;
    else if (a.tipo === "deposito_devuelto") comprometido -= a.monto;
    else if (a.tipo === "deposito_aplicado") comprometido -= a.monto;
  });
  return { saldo, comprometido, propio: saldo - comprometido };
}
